import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import Button from "../Components/Button";
import PageBanner from "../Components/PageBanner";
import { servicesData, getAgentsData } from "../Constants/Data";

const createSlug = (firstName = "", lastName = "") => {
  return `${String(firstName).toLowerCase().trim()}-${String(lastName)
    .toLowerCase()
    .trim()}`;
};

// Treat these as "empty" aboutme values
const isInvalidAbout = (value) => {
  const v = String(value ?? "")
    .trim()
    .toLowerCase();

  return (
    !v ||
    [
      "n/a",
      "na",
      "not available",
      "none",
      "null",
      "undefined",
      "-",
      "--",
    ].includes(v)
  );
};

export default function TeamDetails() {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { name: nameParam } = useParams();

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const agents = await getAgentsData();
        const slug = String(nameParam ?? "").toLowerCase();

        const foundAgent = agents.find((a) => {
          const aSlug = createSlug(a?.firstname, a?.lastname);
          return aSlug === slug;
        });

        setAgent(foundAgent || null);
      } catch (error) {
        setAgent(null);
      } finally {
        setLoading(false);
      }
    };

    if (nameParam) fetchAgent();
    else setLoading(false);
  }, [nameParam]);

  const fullName = useMemo(() => {
    if (!agent) return "";
    return `${agent?.firstname ?? ""} ${agent?.lastname ?? ""}`.trim();
  }, [agent]);

  const aboutText = useMemo(() => {
    if (!agent) return "";
    if (isInvalidAbout(agent?.aboutme)) return fullName || "Unknown";
    return String(agent.aboutme).trim();
  }, [agent, fullName]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!nameParam || !agent) {
    return (
      <p className="text-center text-danger py-5">
        Agent Not Found or Invalid URL
      </p>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {fullName ? `${fullName} | ` : ""}
          Urban View Real Estate PVT LTD
        </title>

        <meta
          name="description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta
          name="keywords"
          content="Affordable Homes for Sale in South Australia, Affordable Homes for Sale in Melbourne, Affordable Homes for Sale in Queensland, Development Sites for Sale in Sydney, Cheap Rent a Home in Penrith, House Buying in Western Australia, Cheap Duplex Home in Tamworth, Affordable duplex Home in Armidale, Houses for Rental in St Marys, Investement Property in Queensland, Best Investment Property Sale in Brisbane, Best Investment Property Sale in Rouse Hill, Box Hill, Marsden Park, Vineyard, Investment Property Buy in Austral, Investment Property Buy in Tallawong, Investment Property Buy in Oran Park, Investment Property Buy in Leppington"
        />
        <meta name="author" content="Urban View Real Estate PVT LTD" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://urbanviewre.com/${fullName ? `${fullName} | ` : ""}`} />

        <meta property="og:title" content="Urban View Real Estate PVT LTD" />
        <meta
          property="og:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta property="og:site_name" content="Urban View Real Estate" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://urbanviewre.com" />
        <meta
          property="og:image"
          content="https://urbanviewre.com/assets/logo-2-CGgGTWZk.png"
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Urban View Real Estate PVT LTD" />
        <meta
          name="twitter:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta
          name="twitter:image"
          content="https://urbanviewre.com/assets/logo-2-CGgGTWZk.png"
        />
        <meta name="twitter:site" content="@urbanviewre" />
        <meta name="twitter:creator" content="@urbanviewre" />

        <meta
          property="fb:admins"
          content="https://www.facebook.com/profile.php?id=61559035348971"
        />

        <meta name="instagram:title" content="Urban View Real Estate PVT LTD" />
        <meta
          name="instagram:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta name="instagram:site" content="@urbanviewrealestate" />
      </Helmet>

      <PageBanner
        title="Team Details"
        breadcrumbs={[
          { label: "Home", href: "/", icon: "bi-house-door-fill" },
          { label: "Team", href: "/team" },
          { label: "Team Details", active: true },
        ]}
      />

      <section className="py-5 mb-5">
        <div className="container py-5">
          <div className="row">
            {/* Left Card */}
            <div className="col-12 col-lg-3 mb-4">
              <div
                className="card text-center rounded-0 mx-auto"
                style={{ maxWidth: "320px" }}
              >
                {agent.profileimg ? (
                  <div className="ratio ratio-1x1 image-container overflow-hidden">
                    <img
                      src={agent.profileimg}
                      alt={fullName || "Agent"}
                      className="card-img-top object-fit-cover"
                    />
                  </div>
                ) : (
                  <div className="d-flex align-items-center justify-content-center bg-light ratio ratio-1x1">
                    <span className="text-muted small">No Image</span>
                  </div>
                )}

                <div className="card-body px-3">
                  <h6 className="fw-bold mb-1">{fullName || "Unknown"}</h6>

                  <p className="text-primary small fw-semibold mb-3">
                    {agent.designation || "No Designation"}
                  </p>

                  <div className="d-flex justify-content-center gap-3">
                    {agent.social_media?.facebook && (
                      <a
                        href={agent.social_media.facebook}
                        className="text-dark"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                      >
                        <i className="bi bi-facebook fs-5"></i>
                      </a>
                    )}

                    {agent.social_media?.twitter && (
                      <a
                        href={agent.social_media.twitter}
                        className="text-dark"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Twitter"
                      >
                        <i className="bi bi-twitter fs-5"></i>
                      </a>
                    )}

                    {agent.social_media?.linkedin && (
                      <a
                        href={agent.social_media.linkedin}
                        className="text-dark"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="LinkedIn"
                      >
                        <i className="bi bi-linkedin fs-5"></i>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content */}
            <div className="col-12 col-lg-9">
              {/* About */}
              <p className="text-muted m-0">{aboutText}</p>

              {/* Details */}
              <div className="row pt-5">
                <div className="col-md-6">
                  <p>
                    <strong>Position:</strong>{" "}
                    {agent.designation || "Not available"}
                  </p>
                  <p>
                    <strong>Phone:</strong> {agent.mobile || "Not available"}
                  </p>
                </div>
                <div className="col-md-6">
                  <p>
                    <strong>Email:</strong> {agent.email || "Not available"}
                  </p>
                  <p>
                    <strong>Fax:</strong> {agent.landline || "Not available"}
                  </p>
                </div>
              </div>

              {/* Form */}
              <div className="card border-0 rounded-0 shadow-sm p-3 p-md-4 p-lg-5 mt-4">
                <h3 className="fw-bold border-start border-3 border-primary ps-3 mb-4">
                  Contact for any Inquiry
                </h3>

                <form>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control border rounded-0 py-3"
                        placeholder="Enter your name"
                        required
                      />
                    </div>

                    <div className="col-md-6">
                      <input
                        type="email"
                        className="form-control border rounded-0 py-3"
                        placeholder="Enter email address"
                        required
                      />
                    </div>

                    <div className="col-md-6">
                      <select
                        className="form-select border rounded-0 py-3"
                        defaultValue=""
                        required
                      >
                        <option value="" disabled>
                          Select Service Type
                        </option>
                        {servicesData.map((service) => (
                          <option key={service.id} value={service.title}>
                            {service.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control border rounded-0 py-3"
                        placeholder="Enter phone number"
                        required
                      />
                    </div>

                    <div className="col-12">
                      <textarea
                        className="form-control border rounded-0 py-3"
                        rows="3"
                        placeholder="Enter message"
                        required
                      ></textarea>
                    </div>

                    <div className="col-12">
                      <Button
                        text="GET A FREE SERVICE"
                        className="text-muted"
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>
            {/* End Right */}
          </div>
        </div>
      </section>
    </>
  );
}